import React, { forwardRef, Reducer, useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { ApolloError, useApolloClient, useMutation } from '@apollo/client';

import type {
  AppFragmentFragment as AppFragment,
  DeleteProductImageMutation,
  DeleteProductImageMutationVariables,
  Menu,
  MenuOption,
  PosProductQuery,
  PosProductQueryVariables,
  Product,
  ProductLinkQuery,
  ProductLinkQueryVariables,
  ProductVariablesByApp,
  UpdateProductImageMutation,
  UpdateProductImageMutationVariables,
  UpdateProductMutation,
  UpdateProductMutationVariables,
  Venue
} from 'generated/graphql';
import {
  type QueuedProductImageUpdate,
  type ReducerAction,
  type ReducerActionOptions,
  type ReducerState,
  SnackBarTypeEnum
} from 'types';
import { AppFragment as APP_FRAGMENT } from 'queries/App';
import { PublishMenuMutation as PUBLISH_MENU_MUTATION } from 'queries/Menu';
import {
  DeleteProductImageMutation as DELETE_PRODUCT_IMAGE_MUTATION,
  posProductQuery as POS_PRODUCT_QUERY,
  ProductFragment as PRODUCT_FRAGMENT,
  ProductLinkQuery as PRODUCT_LINK_QUERY,
  UpdateProductImageMutation as UPDATE_PRODUCT_IMAGE_MUTATION,
  UpdateProductMutation as UPDATE_PRODUCT_MUTATION
} from 'queries/Product';
import { listAppsInString, sanitiseUrlParam } from 'utils';
import DetailsHeader from 'components/SingleItem/DetailsHeader';
import SnackbarOutlet from 'components/Snackbar/SnackbarOutlet';
import DetailsFooter from 'components/SingleItem/DetailsFooter';
import { useModalContext } from 'common/Context/ModalContext';
import { getEnvironmentVariables } from 'services/environment-variables/environment-variables';
import { ItemContext } from 'components/SingleItem/ItemContext';
import { useAppContext } from 'AppContext';
import { convertToProductInput } from 'utils/menuConversion';
import useScreenWidthQuery from 'common/Hooks/UseScreenWidthQuery';
import { sanitizeUrl } from '@braintree/sanitize-url';
import { AmplitudeEventCategories, AmplitudeEventProperties, logAmplitudeEvent } from 'utils/Amplitude';
import General from './General';
import OptionGroups from './OptionGroups/OptionGroups';
import { WindowBreakpoints } from '../../utils/DocumentHelper';
import { fromVirtualBrandId, venueHasBrands } from '../../utils/brand';

import styles from './Details.module.scss';
import { deepDiffWithIgnores } from '../../utils/Diff';

export type VenueWithMenus = Omit<Venue, 'menus'> & { menus: Menu[] };

type FileSubmitType = [file: File, optimisticURL: string];

interface Props {
  currentVenue?: VenueWithMenus | undefined;
  dietaryEnums: string[];
  isTest?: string;
  queuedProductImageUpdates?: QueuedProductImageUpdate[];
  updateQueuedProductImageUpdates?: (updatedQueue: QueuedProductImageUpdate[]) => void;
}

const reducer: Reducer<ReducerState, ReducerAction> = (state, action) => {
  if (action.key === 'item') {
    return cloneDeep(action.payload);
  }

  if (!state) {
    return state;
  }

  if (action.key === 'imageUri-all') {
    return {
      ...state,
      variablesByApp: state.variablesByApp.map((vba) => ({
        ...vba,
        imageUri: action.payload
      }))
    };
  }

  if (action.key === 'imageUri-single') {
    return {
      ...state,
      variablesByApp: state.variablesByApp.map((vba) => {
        if (vba.app.id === action.payload.appId) {
          return {
            ...vba,
            imageUri: action.payload.nextValue
          };
        }

        return vba;
      })
    };
  }

  return {
    ...state,
    [action.key]: action.payload
  };
};

const Details = forwardRef<HTMLDivElement, Props>(
  ({ currentVenue, dietaryEnums, isTest, queuedProductImageUpdates, updateQueuedProductImageUpdates }: Props, ref) => {
    const { itemId, menuId } = useParams();
    const { DOORDASH_ID } = getEnvironmentVariables();

    const [updateProduct] = useMutation<UpdateProductMutation, UpdateProductMutationVariables>(UPDATE_PRODUCT_MUTATION);
    const [publishMenu] = useMutation(PUBLISH_MENU_MUTATION);
    const [updateProductImage] = useMutation<UpdateProductImageMutation, UpdateProductImageMutationVariables>(
      UPDATE_PRODUCT_IMAGE_MUTATION
    );
    const [deleteProductImage] = useMutation<DeleteProductImageMutation, DeleteProductImageMutationVariables>(
      DELETE_PRODUCT_IMAGE_MUTATION
    );
    const [newImageFile, setNewImageFile] = useState<FileSubmitType | null>();

    const { draftMenus, setDraftMenus, modifiedMenuIds } = useAppContext();

    const { openModal, openSnackBar, activeSnackBars, closeSnackBar } = useModalContext();
    const [infoSnackBarId, setInfoSnackBarId] = useState<string | null>(null);

    const usesDraft = !!menuId;

    const sanitisedItemId = sanitiseUrlParam(itemId);
    const sanitisedMenuId = sanitiseUrlParam(menuId);

    const currentAppId =
      sanitisedMenuId && draftMenus ? draftMenus.find((menu) => menu.mmId === sanitisedMenuId)?.apps[0].id : undefined;

    const setDraftMenu = useCallback(
      (updatedMenu: Menu) => {
        const draftMenusCopy = cloneDeep(draftMenus);
        if (draftMenusCopy && sanitisedMenuId) {
          const draftMenuIndex = draftMenusCopy.findIndex((menu) => menu.mmId === sanitisedMenuId);
          if (draftMenuIndex > -1) {
            draftMenusCopy[draftMenuIndex] = updatedMenu;
            setDraftMenus(draftMenusCopy);
          }
        }
      },
      [draftMenus, sanitisedMenuId, setDraftMenus]
    );
    const { cache, query } = useApolloClient();

    const cacheKey = cache.identify({ __typename: 'Product', mmId: sanitisedItemId });
    if (!cacheKey) {
      // eslint-disable-next-line no-console
      console.error(`Unable to identify Product with mmId ${sanitisedItemId}`);
    }

    const publishedItem: Product | null = useMemo(() => {
      if (!currentVenue) return null;

      if (!cacheKey) {
        return null;
      }

      return cache.readFragment(
        {
          id: cacheKey,
          fragment: PRODUCT_FRAGMENT,
          fragmentName: 'ProductFragment'
        },
        true
      );
    }, [cacheKey, cache, currentVenue]);

    const isUnpublished: boolean = useMemo(() => {
      const draftMenu = draftMenus?.find((menu) => menu.mmId === sanitisedMenuId);

      if (draftMenu && sanitisedItemId.startsWith('doshiinew')) {
        return true;
      }
      return false;
    }, [sanitisedItemId, draftMenus, sanitisedMenuId]);

    const getItemInitialState = useCallback((): Product | null => {
      let item = publishedItem;
      if (usesDraft) {
        const clonedDraftMenu = cloneDeep(draftMenus);
        const draftMenu = clonedDraftMenu.find((menu) => menu.mmId === sanitisedMenuId);
        const draftItem = draftMenu?.products.find((product) => product.mmId === sanitisedItemId);
        if (draftItem) item = draftItem;
      }
      return item;
    }, [draftMenus, publishedItem, sanitisedItemId, sanitisedMenuId, usesDraft]);

    const [itemState, dispatch] = useReducer(reducer, getItemInitialState());

    const [productLink, setProductLink] = useState<NonNullable<ProductLinkQuery['productLink']> | null>(null);

    // Calls the Product Link query if all the required variables are available
    useEffect(() => {
      if (!currentVenue?.hashedId || !itemState?.posId || !itemState?.variablesByApp[0]?.app.id) {
        return;
      }

      query<ProductLinkQuery, ProductLinkQueryVariables>({
        query: PRODUCT_LINK_QUERY,
        variables: {
          posId: itemState.posId,
          appId: itemState.variablesByApp[0].app.id,
          locationId: currentVenue.hashedId
        }
      })
        .then(({ data }) => {
          setProductLink(data.productLink || null);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error occurred while getting Product Link', error);
        });
      /**
       * The `react-hooks/exhaustive-deps` rule is disabled here because the dependency
       * `itemState?.variablesByApp[0]?.app.id` is being used in the effect. However, adding
       * this specific property to the dependency array would cause ESLint to complain,
       * preferring `itemState?.variablesByApp` instead. Having `itemState?.variablesByApp`
       * as a dependency would be too excessive and could lead to unnecessary re-renders or
       * performance issues.
       */
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVenue?.hashedId, itemState?.posId, itemState?.variablesByApp[0]?.app.id, query]);

    const [posProduct, setPosProduct] = useState<NonNullable<PosProductQuery['posProduct']> | null>(null);
    // Calls the Product Link query if all the required variables are available
    useEffect(() => {
      if (!currentVenue?.hashedId || !itemState?.posId) {
        return;
      }

      query<PosProductQuery, PosProductQueryVariables>({
        query: POS_PRODUCT_QUERY,
        variables: { locationId: currentVenue.hashedId, posId: `${itemState.posId}` }
      })
        .then(({ data }) => {
          setPosProduct(data.posProduct || null);
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Error occurred while getting POS Product', error);
        });
    }, [currentVenue?.hashedId, itemState?.posId, query]);

    // Let draft menu product update variablesByApp to reflect state of toggle
    // Don't update itemState directly since we don't want to lose reference to previously set prices when untoggling
    const updateDraftPricesOnToggle = useCallback(
      (checked: boolean) => {
        if (!usesDraft) return;
        const draftMenusCopy = cloneDeep(draftMenus);
        if (!draftMenusCopy || !itemState || !sanitisedMenuId) return;
        const draftMenu = draftMenusCopy?.find((menu) => menu.mmId === sanitisedMenuId);

        if (!draftMenu) return;
        const draftProduct =
          draftMenu.products[draftMenu.products.findIndex((product: Product) => product.mmId === itemState.mmId)];
        if (!draftProduct) return;
        if (checked)
          draftProduct.variablesByApp.forEach((vba) => {
            vba.price = itemState.variablesByApp[0].price;
          });
        else {
          draftProduct.variablesByApp.forEach((vba) => {
            const itemStateVBA = itemState.variablesByApp.find(({ app }) => app.id === vba.app.id);
            if (itemStateVBA) vba.price = itemStateVBA.price;
          });
        }
        setDraftMenus(draftMenusCopy);
      },
      [draftMenus, itemState, sanitisedMenuId, setDraftMenus, usesDraft]
    );

    // Update Draft Menu To reflect Item State
    const syncDraftMenuOnChange = useCallback(() => {
      if (!usesDraft) return;
      const draftMenusCopy = cloneDeep(draftMenus);

      if (!draftMenusCopy || !itemState || !sanitisedMenuId) return;
      const draftMenu = draftMenusCopy?.find((menu) => menu.mmId === sanitisedMenuId);

      if (!draftMenu) return;
      draftMenu.products[draftMenu.products.findIndex((product: Product) => product.mmId === itemState.mmId)] =
        itemState;
      setDraftMenus(draftMenusCopy);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemState, sanitisedMenuId]);

    useEffect(syncDraftMenuOnChange, [syncDraftMenuOnChange]);

    const [isSameOnAllApps, setIsSameOnAllApps] = useState<boolean>(false);
    const hasAssessedPriceEquality = useRef<boolean>(false);

    const handleSetSamePriceOnAllApps = (): void => {
      const checked = !isSameOnAllApps;
      setIsSameOnAllApps(checked);
      if (usesDraft) updateDraftPricesOnToggle(checked);
    };

    const itemContainsSurcharge = useMemo(() => {
      return itemState?.variablesByApp.some((vba) => vba.surcharge && vba.surcharge.amount > 0);
    }, [itemState]);

    // Set Same Price on All Apps Toggle
    useEffect(() => {
      if (itemState?.variablesByApp && !hasAssessedPriceEquality.current) {
        hasAssessedPriceEquality.current = true;

        if (itemState.variablesByApp.length) {
          const firstAppPrice = itemState.variablesByApp[0].price;

          let areAllPricesSame = true;

          itemState.variablesByApp.forEach((appVariable: ProductVariablesByApp) => {
            if (appVariable.price !== firstAppPrice) {
              areAllPricesSame = false;
            }
          });
          if (itemContainsSurcharge && !areAllPricesSame) {
            areAllPricesSame = false;
          }
          setIsSameOnAllApps(areAllPricesSame);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemState]);

    // Set item state to published value if no item state exists
    useEffect(() => {
      const item = getItemInitialState();
      if (item && !itemState) {
        dispatch({ key: 'item', payload: item });
      }
    }, [dispatch, publishedItem, itemState, getItemInitialState]);

    const [innerWidth] = useScreenWidthQuery();
    const [isMobileView, setIsMobileView] = useState(innerWidth < WindowBreakpoints.SM);

    useEffect(() => {
      const isMobileViewNew = innerWidth < WindowBreakpoints.SM;
      if (isMobileViewNew !== isMobileView) setIsMobileView(isMobileViewNew);
    }, [innerWidth, isMobileView]);

    // Handle "unpublished changes" Snackbar visibility
    useEffect(() => {
      const draftMenu = draftMenus.find(({ mmId }) => mmId === sanitisedMenuId);
      if (!draftMenu || !usesDraft) return;

      const { neverPublished } = draftMenu;
      const desktopText = neverPublished
        ? "To view your edits in the apps, head to the top of your menu and hit 'Publish' or 'Save' if you're not ready to go live."
        : "To see your changes in the apps, be sure to hit 'Publish' from the top of the menu.";
      const mobileText = neverPublished ? 'Unpublished menu' : 'Unpublished changes';
      const text = isMobileView ? mobileText : desktopText;
      // if from changed state to unchanged
      if (infoSnackBarId && !modifiedMenuIds.includes(sanitisedMenuId)) {
        closeSnackBar(infoSnackBarId);
        setInfoSnackBarId(null);
        return;
      }
      // if from unchanged state to changed
      if (!infoSnackBarId && modifiedMenuIds.includes(sanitisedMenuId)) {
        if (itemState) {
          setInfoSnackBarId(openSnackBar(text, `doshiidraft${itemState.mmId}`, undefined, SnackBarTypeEnum.INFO, -1));
        }
        return;
      }
      if (infoSnackBarId) {
        // Incorrect text for screen size check
        const existingSnackBar = activeSnackBars.find((snack) => snack.id === infoSnackBarId);
        if (!existingSnackBar) return;
        if (existingSnackBar.text !== text) {
          closeSnackBar(infoSnackBarId);
          setInfoSnackBarId(null);
        }
      }
    }, [
      activeSnackBars,
      closeSnackBar,
      draftMenus,
      infoSnackBarId,
      isMobileView,
      itemId,
      itemState,
      modifiedMenuIds,
      openSnackBar,
      sanitisedMenuId,
      usesDraft
    ]);

    const updateOptions = useCallback(
      (dispatchArguments: ReducerActionOptions, changedOption?: MenuOption) => {
        if (!itemState?.options) {
          return;
        }
        dispatch(dispatchArguments);
        if (usesDraft && changedOption) {
          const draftMenu = cloneDeep(draftMenus?.find((menu) => menu.mmId === sanitisedMenuId));
          const draftProduct = draftMenu?.products.find((product) => product.mmId === sanitisedItemId);
          const draftOptionIndex = draftProduct
            ? draftProduct.options.findIndex((option) => option.posId === changedOption.posId)
            : -1;
          if (!draftMenu || !draftProduct || draftOptionIndex === -1) {
            return;
          }
          draftProduct.options[draftOptionIndex] = changedOption;
          setDraftMenu(draftMenu);
        }
      },
      [draftMenus, itemState?.options, sanitisedItemId, sanitisedMenuId, setDraftMenu, usesDraft]
    );

    const { posId } = itemState || {};

    const [activeTab, setActiveTab] = useState<string>('general');
    const [hasScrolled, setHasScrolled] = useState<boolean>(false);
    const [isAvailabilityLoading, setIsAvailabilityLoading] = useState<boolean>(false);

    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
      if (activeTab === 'optionGroups') {
        const isInMenus = sanitizeUrl(location.pathname).includes('/menus/');
        logAmplitudeEvent({
          name: 'view item option groups',
          category: isInMenus
            ? AmplitudeEventCategories.MM_SIDE_PANEL_MENU
            : AmplitudeEventCategories.MM_SIDE_PANEL_ITEMS,
          properties: {
            [AmplitudeEventProperties.FROM]: `${isInMenus ? 'menu' : 'items'} side panel`
          }
        });
      }
    }, [activeTab, location.pathname]);

    const closeModal = useCallback((): void => {
      const { pathname } = location;
      if (isAvailabilityLoading) return;
      const pathnameWithoutHash = pathname.split('/items')[0];
      if (infoSnackBarId) closeSnackBar(infoSnackBarId);

      navigate(pathnameWithoutHash);
    }, [closeSnackBar, infoSnackBarId, isAvailabilityLoading, location, navigate]);

    const closeRef = useRef<HTMLButtonElement>(null);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>): void => {
      const targetAsDiv = e.target as HTMLDivElement;

      const bounding = targetAsDiv.getBoundingClientRect();
      const deficit = targetAsDiv.scrollHeight - bounding.height;

      const scrollBottom = deficit - targetAsDiv.scrollTop;

      if (targetAsDiv.scrollTop > 10) {
        setHasScrolled(true);
      } else if (scrollBottom > 10) {
        setHasScrolled(false);
      }
    };

    const getRelevantAppIds = useCallback((): string[] => {
      if (!itemState) return [];
      const appIds: string[] = [];

      itemState.variablesByApp.forEach((appVariable: ProductVariablesByApp) => {
        appIds.push(appVariable.app.id);
      });

      return appIds;
    }, [itemState]);

    const hasBrands = useMemo(() => venueHasBrands(currentVenue?.menus ?? []), [currentVenue?.menus]);

    const handleSubmit = useCallback(async (): Promise<{
      error: ApolloError;
      currentVenue: Venue | undefined | null;
    } | null> => {
      if (!itemState || !publishedItem) {
        return null;
      }

      let returnError: { error: ApolloError; currentVenue: Venue | undefined | null } | null = null;
      const finalProduct: Product = cloneDeep(itemState);
      if (isSameOnAllApps && finalProduct.variablesByApp.length)
        finalProduct.variablesByApp.forEach((vba) => (vba.price = finalProduct.variablesByApp[0].price));

      const finalFailedAppIdsAndReqIds: { failedAppIds: string[]; requestIds: string[] } = {
        failedAppIds: [],
        requestIds: []
      };

      const isProductDetailsUpdated = !!deepDiffWithIgnores(finalProduct, publishedItem, ['mmId', 'source']).length;
      const isImageUpdated = !!newImageFile || newImageFile === null;
      const isProductUpdated = isImageUpdated || isProductDetailsUpdated;

      if (newImageFile) {
        if (!posId) {
          throw new Error(`Expected posId to be defined`);
        }
        if (!currentVenue?.hashedId) {
          throw new Error(`Expected currentVenue.hashedId to be defined`);
        }

        const [imageFile] = newImageFile;
        await updateProductImage({
          variables: {
            posId,
            image: {
              file: imageFile,
              docType: imageFile.type
            },
            locationId: currentVenue?.hashedId,
            appIds: getRelevantAppIds()
          },
          update: (cache, { data, extensions }) => {
            try {
              if (!data) {
                throw new Error('Expected result.data');
              }

              const { updateProductImage } = data;
              if (!updateProductImage) {
                throw new Error('Expected result.data.updateProductImage');
              }

              const { newImageUrl, failedAppIds } = updateProductImage;

              if (failedAppIds.length) {
                failedAppIds.forEach((failedAppId: string) => {
                  if (!finalFailedAppIdsAndReqIds.failedAppIds.includes(failedAppId))
                    finalFailedAppIdsAndReqIds.failedAppIds.push(failedAppId);
                });
                finalFailedAppIdsAndReqIds.requestIds.push(extensions?.requestId);
              } else if (newImageUrl) {
                const id = cache.identify(finalProduct);
                if (!id) {
                  throw new Error(`Expected to identify Product with mmId ${finalProduct.mmId}`);
                }

                cache.modify({
                  id,
                  fields: {
                    imageUri: () => newImageUrl,
                    variablesByApp: (existingVariablesByAppRefs) => {
                      if (!Array.isArray(existingVariablesByAppRefs)) {
                        throw new Error(`Expected existingVariablesByAppRefs to be an array`);
                      }

                      return existingVariablesByAppRefs.map((existingVariablesByAppRef) => {
                        return {
                          ...existingVariablesByAppRef,
                          imageUri: newImageUrl
                        };
                      });
                    }
                  }
                });
              }
            } catch (error) {
              // Note: Apollo Client update() does not propagate errors
              /* eslint-disable-next-line no-console */
              console.error('Error during updateProductImage update():', error);
            }
          },
          // Only mutation errors are handled here
          onError: (error) => {
            returnError = { error, currentVenue };
          }
        });
      } else if (newImageFile === null) {
        if (!currentVenue?.hashedId) {
          throw new Error(`Expected currentVenue.hashedId to be defined`);
        }

        await deleteProductImage({
          variables: {
            posId: publishedItem?.posId,
            appIds: getRelevantAppIds(),
            locationId: currentVenue?.hashedId
          },
          update: (cache, { data, extensions }) => {
            try {
              if (!data) {
                throw new Error('Expected result.data');
              }

              const { deleteProductImage } = data;
              if (!deleteProductImage) {
                throw new Error('Expected result.data.deleteProductImage');
              }

              const { failedAppIds } = deleteProductImage;

              if (failedAppIds.length) {
                failedAppIds.forEach((failedAppId: string) => {
                  if (!finalFailedAppIdsAndReqIds.failedAppIds.includes(failedAppId))
                    finalFailedAppIdsAndReqIds.failedAppIds.push(failedAppId);
                });
                finalFailedAppIdsAndReqIds.requestIds.push(extensions?.requestId);
              } else {
                const id = cache.identify(finalProduct);
                if (!id) {
                  throw new Error(`Expected to identify Product with mmId ${finalProduct.mmId}`);
                }

                cache.modify({
                  id,
                  fields: {
                    imageUri: (_, { DELETE }) => DELETE,
                    variablesByApp: (existingVariablesByAppRefs) => {
                      if (!Array.isArray(existingVariablesByAppRefs)) {
                        throw new Error(`Expected existingVariablesByAppRefs to be an array`);
                      }

                      return existingVariablesByAppRefs.map((existingVariablesByAppRef) => {
                        return {
                          ...existingVariablesByAppRef,
                          imageUri: null
                        };
                      });
                    }
                  }
                });
              }
            } catch (error) {
              // Note: Apollo Client update() does not propagate errors
              /* eslint-disable-next-line no-console */
              console.error('Error during deleteProductImage update():', error);
            }
          },
          // Only mutation errors are handled here
          onError: (error) => {
            returnError = { error, currentVenue };
          }
        });
      }

      if (isProductDetailsUpdated) {
        if (!currentVenue) {
          throw new Error(`Expected currentVenue to be defined`);
        }

        const productInput = convertToProductInput(finalProduct, publishedItem);

        await updateProduct({
          variables: {
            product: productInput,
            locationId: currentVenue.hashedId
          },
          update: (cache, { data, extensions }) => {
            try {
              if (!data) {
                throw new Error('Expected result.data');
              }
              const {
                updateProduct: { failedApps: failedAppIds, product }
              } = data;
              if (failedAppIds.length) {
                // Mutation result has errors
                finalFailedAppIdsAndReqIds.failedAppIds = failedAppIds;
                finalFailedAppIdsAndReqIds.requestIds.push(extensions?.requestId);
              } else if (product) {
                // Mutation result has no errors
                const prevMmId = finalProduct.mmId;
                const nextMmId = product.mmId;
                // It is expected that nextMmId is the same as prevMmId
                if (nextMmId !== prevMmId) {
                  throw new Error(`Expected new mmId to be equal to old mmId: new: ${nextMmId}, old: ${prevMmId}`);
                }
                /**
                 * Updating cache Product is done automatically by Apollo Client
                 * when mutation result contains Product fragment
                 */
              }
            } catch (error) {
              // Note: Apollo Client update() does not propagate errors
              /* eslint-disable-next-line no-console */
              console.error('Error during updateProduct update():', error);
            }
          },
          // Only mutation errors are handled here
          onError: (error) => {
            returnError = { error, currentVenue };
          }
        });
      }

      if (isProductUpdated && !finalFailedAppIdsAndReqIds.failedAppIds.length) {
        await Promise.all(
          finalProduct.variablesByApp.map(async (variableByApp) => {
            const { id: appId } = variableByApp.app;
            await publishMenu({
              variables: {
                venueId: currentVenue?.hashedId,
                appId
              },
              // Only mutation errors are handled here
              onError: (error) => {
                if (!finalFailedAppIdsAndReqIds.failedAppIds.includes(appId)) {
                  finalFailedAppIdsAndReqIds.failedAppIds.push(appId);
                }
                const requestId =
                  error.graphQLErrors[0]?.extensions &&
                  'requestId' in error.graphQLErrors[0].extensions &&
                  typeof error.graphQLErrors[0].extensions.requestId === 'string'
                    ? error.graphQLErrors[0].extensions.requestId
                    : undefined;
                if (requestId) {
                  finalFailedAppIdsAndReqIds.requestIds.push(requestId);
                }
                returnError = { error, currentVenue };
              }
            });
          })
        );
      }

      if (finalFailedAppIdsAndReqIds.failedAppIds.length) {
        const failedApps = finalFailedAppIdsAndReqIds.failedAppIds.reduce<AppFragment[]>((acc, appId) => {
          const cacheKey = cache.identify({ __typename: 'App', id: appId });
          if (!cacheKey) {
            // eslint-disable-next-line no-console
            console.error(`Unable to identify App with id ${appId}`);

            return acc;
          }

          const cachedFragment = cache.readFragment<AppFragment>({
            id: cacheKey,
            fragment: APP_FRAGMENT,
            fragmentName: 'AppFragment'
          });

          if (cachedFragment) {
            acc.push(cachedFragment);
          }

          return acc;
        }, []);

        const error = new ApolloError({
          extraInfo: {
            failedApps,
            subject: `Product update Failing for ${listAppsInString(failedApps, hasBrands, currentVenue?.name)}`
          },
          graphQLErrors: [
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            {
              extensions: {
                mmCode: 6003,
                requestId: finalFailedAppIdsAndReqIds.requestIds.join(', ')
              }
            }
          ]
        });
        return { error, currentVenue };
      }
      if (returnError) return returnError;

      setDraftMenus([]);
      setTimeout(() => {
        openSnackBar(`Published! Please allow a few minutes for your apps to reflect.`);
      }, 300);
      closeModal();
      return null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      cacheKey,
      cache,
      closeModal,
      currentVenue,
      deleteProductImage,
      getRelevantAppIds,
      isSameOnAllApps,
      itemState,
      newImageFile,
      openSnackBar,
      posId,
      publishedItem,
      setDraftMenus,
      updateProduct,
      updateProductImage,
      hasBrands,
      publishMenu
    ]);

    // Show DoorDash warning modal when product uses DoorDash and next image is truthy
    useEffect(() => {
      if (!itemState) {
        return;
      }

      if (newImageFile === undefined) {
        return;
      }

      if (newImageFile === null) {
        return;
      }

      const itemUsesDoorDash = itemState.variablesByApp.some(
        (vba) => fromVirtualBrandId(vba.app.id).appId === DOORDASH_ID
      );

      if (!itemUsesDoorDash) {
        return;
      }

      openModal({
        type: 'DOORDASH_WARNING'
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemState?.variablesByApp, newImageFile, openModal, DOORDASH_ID]);

    // Handle Image updates
    useEffect(() => {
      if (!itemState) return;
      if (newImageFile !== undefined) {
        if (usesDraft) {
          if (!queuedProductImageUpdates || !updateQueuedProductImageUpdates) return;
          const updatedList = queuedProductImageUpdates.filter((update) => update.posId !== itemState.posId);
          if (newImageFile === null) {
            dispatch({ key: 'imageUri-all', payload: null });
            updatedList.push({
              posId: itemState.posId,
              appIds: getRelevantAppIds(),
              localURL: null,
              image: null
            });
          } else {
            const [imageFile, optimisticURL] = newImageFile;
            dispatch({ key: 'imageUri-all', payload: optimisticURL });
            updatedList.push({
              posId: itemState.posId,
              appIds: getRelevantAppIds(),
              localURL: optimisticURL,
              image: { file: imageFile, docType: imageFile.type }
            });
          }

          updateQueuedProductImageUpdates(updatedList);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [itemState?.posId, newImageFile, usesDraft]);

    useEffect(() => {
      closeRef.current?.focus();

      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = 'auto';
      };
    }, []);

    const handleConfirm = (): void => {
      if (!currentVenue || !itemState) return;

      const invalidApps = itemState.variablesByApp.filter((vba) => Number.isNaN(vba.price)).map(({ app }) => app);
      logAmplitudeEvent({
        name: 'click publish item',
        category: AmplitudeEventCategories.MM_SIDE_PANEL_ITEMS,
        properties: {
          [AmplitudeEventProperties.ITEM_POS_ID]: itemState?.posId || '',
          [AmplitudeEventProperties.APPS]: itemState.variablesByApp.map(({ app }) => app.name),
          [AmplitudeEventProperties.ITEM_NAME]: itemState?.name || ''
        }
      });
      if (isTest) {
        handleSubmit();
      } else {
        if (invalidApps.length) {
          openModal({
            type: 'INVALID_FIELDS_SINGLE_ITEM',
            item: itemState.name,
            affectedApps: invalidApps,
            hasBrands,
            venueName: currentVenue.name
          });
          return;
        }
        openModal({
          type: 'EDIT_CONFIRM',
          item: itemState,
          affectedApps: itemState.variablesByApp.map(({ app }) => app),
          callback: async () => await handleSubmit(),
          hasBrands,
          venueName: currentVenue.name
        });
      }
    };

    return (
      <ItemContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        value={{
          item: itemState,
          productLink,
          posProduct,
          handleChange: dispatch,
          currentVenue
        }}
      >
        <div ref={ref} className={styles.Details} data-testid="details-container" aria-modal="true">
          <button
            className={styles.Close}
            type="button"
            onClick={closeModal}
            data-testid="details-close-button"
            ref={closeRef}
          >
            Close
          </button>
          <section aria-label="item Details" className={styles.Inner}>
            <DetailsHeader
              item={itemState}
              originalItem={publishedItem}
              isUnpublished={isUnpublished}
              handleChange={dispatch}
              closeModal={closeModal}
              handleImageSubmit={setNewImageFile}
              currentVenue={currentVenue}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              hasScrolled={hasScrolled}
              setIsAvailabilityLoading={setIsAvailabilityLoading}
              currentAppId={currentAppId}
            />
            <div data-testid="details-content" className={styles.Content} onScroll={handleScroll}>
              {(() => {
                switch (activeTab) {
                  case 'general':
                    return (
                      <General
                        currentVenue={currentVenue}
                        dietaryEnums={dietaryEnums}
                        handleSetSamePriceOnAllApps={handleSetSamePriceOnAllApps}
                        isSameOnAllApps={isSameOnAllApps}
                        hasBrands={hasBrands}
                      />
                    );
                  case 'optionGroups':
                  default:
                    return (
                      <OptionGroups
                        handleChange={updateOptions}
                        item={itemState}
                        isTest={isTest}
                        isSameOnAllApps={isSameOnAllApps}
                      />
                    );
                }
              })()}
              <div className={styles.PosID}>POS ID {posId}</div>
            </div>
            {!usesDraft ? (
              <DetailsFooter handleConfirm={handleConfirm} closeModal={closeModal} />
            ) : (
              <>
                <div style={{ height: '90px', background: '#F5F5F5' }} />
                <SnackbarOutlet itemId={`doshiidraft${itemState?.mmId}`} />
              </>
            )}
            <SnackbarOutlet itemId={itemState?.mmId} />
          </section>
        </div>
      </ItemContext.Provider>
    );
  }
);

Details.defaultProps = {
  currentVenue: undefined,
  isTest: '',
  queuedProductImageUpdates: [],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  updateQueuedProductImageUpdates: () => {}
};

Details.displayName = 'Details';

export default Details;
